import React, { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { RegisterAccountType } from './types';
import {
  ACTION_COL_CLASS,
  primaryCtaClassName,
  REGISTER_CARD_CLASS,
  RegisterStepFooter,
} from './registerShared';

type RegisterStepAccountTypeProps = {
  value: RegisterAccountType;
  onChange: (v: RegisterAccountType) => void;
  onContinue: () => void;
};

const RegisterStepAccountType: React.FC<RegisterStepAccountTypeProps> = ({
  value,
  onChange,
  onContinue,
}) => {
  const { t } = useTranslation(['auth', 'common']);

  const options = useMemo(
    () =>
      [
        { value: 'company' as const, label: t('auth:register.account_type.company') },
        { value: 'self_employed' as const, label: t('auth:register.account_type.self_employed') },
        { value: 'annual' as const, label: t('auth:register.account_type.annual') },
      ] satisfies { value: Exclude<RegisterAccountType, ''>; label: string }[],
    [t],
  );

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (value) onContinue();
    },
    [value, onContinue],
  );

  return (
    <form className={REGISTER_CARD_CLASS} onSubmit={onSubmit} noValidate>
      <div className="flex flex-col gap-2">
        <h1 className="text-center text-[22px] font-semibold leading-tight text-gray-900">
          {t('auth:register.account_type.title')}
        </h1>
        <p className="text-center text-sm text-gray-600">{t('auth:register.account_type.subtitle')}</p>
      </div>

      <div className="mt-2 border border-[#E2E8F0] divide-y divide-[#E2E8F0] rounded-lg overflow-hidden">
        {options.map((opt) => (
          <label
            key={opt.value}
            className="flex cursor-pointer items-center gap-3 bg-[#F9FAFB] px-4 py-3.5 transition-colors hover:bg-white/50"
          >
            <input
              type="radio"
              name="register-account"
              value={opt.value}
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
              className="h-4 w-4 shrink-0 border-gray-300 text-[#6ECFCF] focus:ring-[#6ECFCF] accent-[#6ECFCF]"
            />
            <span className="text-sm font-medium text-[#131315]">{opt.label}</span>
          </label>
        ))}
      </div>

      <div className={ACTION_COL_CLASS}>
        <button
          type="submit"
          disabled={!value}
          className={`${primaryCtaClassName} disabled:cursor-not-allowed disabled:opacity-50`}
        >
          {t('common:buttons.continue')}
        </button>
      </div>

      <p className="text-center text-sm text-gray-500">
        <Link to="/signin" className="font-medium text-[#0d9488] hover:underline">
          {t('auth:register.account_type.already_have_account')}
        </Link>
      </p>

      <RegisterStepFooter step={1} />
    </form>
  );
};

export default RegisterStepAccountType;
